import React from 'react';

import { GENERATIVE_AI_FEATURE_TOKEN } from '@lumapps/ai-generative-content/constants';
import { useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { isFeatureEnabled } from '@lumapps/features';
import { CropperDialog } from '@lumapps/focal-point/components/CropperDialog';
import type { CropInfo, Point } from '@lumapps/focal-point/types';
import { InsertAltTextDialog } from '@lumapps/lumx-dialogs/components/AltTextDialog';
import { useFilePicker } from '@lumapps/lumx-files/hooks/useFilePicker';
import { useBooleanState } from '@lumapps/lumx-hooks/hooks/useBooleanState';
import { Menubar } from '@lumapps/lumx-menu/components/Menubar';
import { mdiClose, mdiCrop, mdiImage, mdiPencilOutline } from '@lumapps/lumx/icons';
import {
    AspectRatio,
    type FocusPoint,
    Text,
    Theme,
    Thumbnail,
    type ThumbnailProps,
    Uploader,
    UploaderVariant,
} from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { UPLOAD_IMAGE_OPTIONS, type UploadDialogImageOption, type UploadImageOption } from '../../types';
import { UploadImageDialog } from '../UploadImageDialog';

import './index.scss';

export interface EditableFeaturedImageProps {
    /** className to forward */
    className?: string;
    /** Focus point coordinates. */
    focalPoint?: FocusPoint;
    /** The crop info to set on the cropper dialog */
    cropInfo?: CropInfo;
    /** Define the fixed aspect ratio of the crop box */
    cropRatio?: number;
    /** The featured image information */
    image?: { url?: string; name?: string; alt?: string };
    /** Image component to render instead of Thumbnail */
    imageComponent?: React.ReactNode;
    /** Whether uploader is read only */
    isReadOnly?: boolean;
    /** Label of the uploader */
    uploaderLabel?: string;
    /** Label above the uploader / the featured image */
    label?: string;
    /** Scope where this component is used. This prop will be used for tracking purposes */
    scope: string;
    /** additional thumbnail props */
    thumbnailProps?: Partial<ThumbnailProps>;
    /** Upload options to pass to the image upload dialog (FF dependent options will be filtered) */
    uploadDialogOptions: UploadDialogImageOption[];
    /** Featured image url after upload or on init (used on the cropper dialog)  */
    originalImageUrl?: string;
    theme?: Theme;
    /** Callback when user update the alt text of the image */
    onAltTextConfirm?: (altText: string) => void;
    /** Callback when user confirm the crop on the image */
    onCropConfirm?: ({
        focus,
        blob,
        cropInfo,
        file,
    }: {
        focus: Point;
        blob: Blob | null;
        cropInfo?: CropInfo;
        file?: File;
    }) => void;
    /** Callback when user remove the image */
    onRemove: () => void;
}

export const CLASSNAME = 'editable-featured-image';

export const EditableFeaturedImage: React.FC<EditableFeaturedImageProps> = ({
    className,
    cropInfo,
    cropRatio,
    focalPoint,
    image,
    imageComponent,
    isReadOnly,
    originalImageUrl,
    label,
    uploaderLabel: initUploaderLabel,
    scope,
    theme = Theme.light,
    thumbnailProps,
    uploadDialogOptions: initUploadDialogOptions,
    onAltTextConfirm,
    onCropConfirm,
    onRemove,
}) => {
    const { block, element } = useClassnames(CLASSNAME);
    const { get } = useDataAttributes(scope);
    const { translateKey } = useTranslate();
    const isGenerativeAiEnabled = useSelector(isFeatureEnabled(GENERATIVE_AI_FEATURE_TOKEN));

    const [isUploadDialogOpen, , closeUploadDialog, openUploadDialog] = useBooleanState(false);
    const [isCropperDialogOpen, , closeCropperDialog, openCropperDialog] = useBooleanState(false);
    const [isAltTextDialogOpen, , closeAltTextDialog, openAltTextDialog] = useBooleanState(false);

    const uploaderLabel = initUploaderLabel || translateKey(GLOBAL.ADD_FEATURED_IMAGE);
    const labelId = React.useId();
    const aspectRatio = thumbnailProps?.aspectRatio || AspectRatio.wide;

    const uploadDialogOptions: UploadDialogImageOption<false>[] = React.useMemo(() => {
        const options = [];

        for (const option of initUploadDialogOptions) {
            // This option should never be added without the related FF enabled
            if (option.type === UPLOAD_IMAGE_OPTIONS.WITH_AI && !isGenerativeAiEnabled) {
                break;
            }

            options.push({
                ...option,
                onConfirm: (...params: Parameters<typeof option.onConfirm>) => {
                    // This is already typed before and TS has troubles to understand what we are trying to do.
                    (option.onConfirm as (...args: unknown[]) => void)(...(params as unknown[]));
                    closeUploadDialog();
                },
            } as UploadDialogImageOption);
        }

        return options;
    }, [initUploadDialogOptions, isGenerativeAiEnabled, closeUploadDialog]);

    const shouldDisplayUploadDialog = uploadDialogOptions.length > 1;
    const uploadFromComputerOption = uploadDialogOptions.find(
        (option) => option.type === UPLOAD_IMAGE_OPTIONS.FROM_COMPUTER,
    ) as UploadImageOption;

    // Pick from computer input, only used when the upload image dialog shouldn't be displayed
    const { hiddenInput, openPicker } = useFilePicker({
        inputOrigin: CLASSNAME,
        accept: 'image/*',
        onSelectFiles: (files) => {
            if (files.length && uploadFromComputerOption) {
                const file = files[0];
                uploadFromComputerOption.onConfirm(file, { url: URL.createObjectURL(file), file });
            }
        },
    });

    const handleOnAltTextConfirm = (altText: string) => {
        if (onAltTextConfirm) {
            onAltTextConfirm(altText);
            closeAltTextDialog();
        }
    };

    const handleOnCropConfirm = (focus: Point, blob: Blob | null, cropInfo?: CropInfo) => {
        if (onCropConfirm) {
            const file = blob ? new File([blob], image?.name || '') : undefined;
            onCropConfirm({ focus, blob, cropInfo, file });
            closeCropperDialog();
        }
    };

    return (
        <div className={block({ 'read-only': !!isReadOnly }, [className])} {...get({ element: 'featured-image' })}>
            {!isReadOnly && hiddenInput}
            {!isReadOnly && shouldDisplayUploadDialog && (
                <UploadImageDialog
                    options={uploadDialogOptions}
                    isOpen={isUploadDialogOpen}
                    dialogTitle={translateKey(GLOBAL.ADD_FEATURED_IMAGE)}
                    onClose={closeUploadDialog}
                    scope={scope}
                />
            )}
            {label && (
                <div className={element('label-wrapper', ['lumx-text-field__header'])}>
                    <Text as="span" className={element('label', ['lumx-text-field__label'])} id={labelId}>
                        {label}
                    </Text>
                </div>
            )}
            {image?.url && image.url.length > 0 ? (
                <div className={element('cover')}>
                    {imageComponent || (
                        <Thumbnail
                            {...thumbnailProps}
                            alt={image.alt || ''}
                            aspectRatio={aspectRatio}
                            focusPoint={focalPoint}
                            image={image.url}
                            theme={theme}
                            aria-labelledby={label ? labelId : undefined}
                        />
                    )}
                    <Menubar
                        ariaLabel={translateKey(GLOBAL.FEATURED_IMAGE_TOOLBAR)}
                        className={element('toolbar')}
                        theme={theme}
                    >
                        {!!onAltTextConfirm && (
                            <Menubar.Section>
                                <Menubar.Item
                                    onClick={openAltTextDialog}
                                    {...get({
                                        element: 'button',
                                        action: 'open-image-alt-dialog',
                                    })}
                                    tooltipProps={{ label: undefined }}
                                    className={element('alt-button')}
                                >
                                    {translateKey(image.alt ? GLOBAL.EDIT_ALT_TEXT : GLOBAL.ADD_ALT_TEXT)}
                                </Menubar.Item>
                            </Menubar.Section>
                        )}
                        <Menubar.Section>
                            {!!onCropConfirm && (
                                <Menubar.Item
                                    onClick={openCropperDialog}
                                    aria-label={translateKey(GLOBAL.CROPPER_DIALOG_TITLE)}
                                    tooltipLabel={translateKey(GLOBAL.CROPPER_DIALOG_TITLE)}
                                    icon={mdiCrop}
                                    {...get({
                                        element: 'button',
                                        action: 'open-image-crop-dialog',
                                    })}
                                />
                            )}
                            <Menubar.Item
                                onClick={shouldDisplayUploadDialog ? openUploadDialog : openPicker}
                                aria-label={translateKey(GLOBAL.EDIT)}
                                tooltipLabel={translateKey(GLOBAL.EDIT)}
                                icon={mdiPencilOutline}
                                {...get({
                                    element: 'button',
                                    action: 'edit-image',
                                })}
                            />
                        </Menubar.Section>
                        <Menubar.Section>
                            <Menubar.Item
                                onClick={onRemove}
                                aria-label={translateKey(GLOBAL.DELETE)}
                                tooltipLabel={translateKey(GLOBAL.DELETE)}
                                icon={mdiClose}
                                {...get({
                                    element: 'button',
                                    action: 'delete-image',
                                })}
                            />
                        </Menubar.Section>
                    </Menubar>
                    {!!onCropConfirm && (
                        <CropperDialog
                            imageUrl={originalImageUrl || image.url}
                            onConfirm={handleOnCropConfirm}
                            onCancel={closeCropperDialog}
                            isOpen={isCropperDialogOpen}
                            focalPoint={focalPoint}
                            cropInfo={cropInfo}
                            cropRatio={cropRatio}
                        />
                    )}
                    {!!onAltTextConfirm && (
                        <InsertAltTextDialog
                            isOpen={isAltTextDialogOpen}
                            size="tiny"
                            currentAlt={image.alt || ''}
                            image={originalImageUrl || image.url}
                            onChange={handleOnAltTextConfirm}
                            onClose={closeAltTextDialog}
                            scope={scope}
                        />
                    )}
                </div>
            ) : (
                <Uploader
                    className={element('placeholder')}
                    aspectRatio={aspectRatio}
                    variant={thumbnailProps?.variant as UploaderVariant}
                    icon={mdiImage}
                    onClick={shouldDisplayUploadDialog ? openUploadDialog : openPicker}
                    label={uploaderLabel}
                    aria-readonly={isReadOnly}
                    theme={theme}
                    {...get({ element: 'featured-image', action: 'upload' })}
                />
            )}
        </div>
    );
};

EditableFeaturedImage.displayName = 'EditableFeaturedImage';
